// The account's money: top-up invoices (crypto, paid on their payment page)
// and every movement on the balance, linked to our campaigns where we know them.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';
import { getHistory, topUp, type AsomInvoice, type AsomOverview } from '@/api/asoMarket';
import { Card, FieldLabel, INPUT, Notice, fmtDate, usd } from './ui';

const KIND: Record<string, string> = {
  Replenishment: 'Top-up',
  CampaignPayment: 'Campaign paid',
  CampaignRefund: 'Campaign refund',
  CampaignTaskPayment: 'Task paid',
  CampaignTaskCancelCompensation: 'Task cancel refund',
  Withdrawal: 'Withdrawal',
  WithdrawalRefund: 'Withdrawal refund',
  ReferralBonus: 'Referral bonus',
  Transfer: 'Transfer',
};

export function MoneyTab({ overview, onOpenCampaign }: { overview: AsomOverview; onOpenCampaign: (id: number) => void }) {
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const canEdit = useCanEdit();
  const [page, setPage] = useState(0);
  const [amount, setAmount] = useState('100');
  const [invoice, setInvoice] = useState<AsomInvoice | null>(null);
  const history = useQuery({ queryKey: ['aso-market', 'history', page], queryFn: () => getHistory(page * 50) });
  const create = useMutation({
    mutationFn: () => topUp(Number(amount)),
    onSuccess: (d) => { setInvoice(d); qc.invalidateQueries({ queryKey: ['aso-market', 'overview'] }); },
    onError: (e: Error) => toast(e.message),
  });
  const copy = (text: string) => navigator.clipboard.writeText(text).then(() => toast('Copied'));
  const pending = overview.finances?.pending_top_ups ?? [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
      <div className="space-y-4">
        <Card className="p-4 space-y-3">
          <div className="text-sm font-medium">Top up the balance</div>
          <p className="text-xs text-content-subtle">ASO.Market issues a USDT invoice; it is paid from the wallet and lands on the balance after confirmation.</p>
          <div>
            <FieldLabel>Amount, USD</FieldLabel>
            <div className="flex gap-2">
              <input type="number" min={1} className={INPUT} value={amount} onChange={(e) => setAmount(e.target.value)} />
              <Button className="shrink-0 whitespace-nowrap" disabled={!canEdit || !(Number(amount) >= 1)} loading={create.isPending} onClick={() => create.mutate()}>Get invoice</Button>
            </div>
          </div>
          {invoice && <InvoiceCard inv={{ amount: invoice.amount, wallet: invoice.wallet_address, url: invoice.payment_url, expires: invoice.expires_at, network: `${invoice.currency} · ${invoice.network_id}` }} onCopy={copy} />}
        </Card>
        {pending.length > 0 && (
          <Card className="p-4 space-y-3">
            <div className="text-sm font-medium">Open invoices</div>
            {pending.map((p) => (
              <InvoiceCard key={p.id} inv={{ amount: p.amount, wallet: p.wallet_address, url: p.payment_url, expires: p.expires_at, network: null }} onCopy={copy} />
            ))}
          </Card>
        )}
        {(overview.finances?.waiting ?? 0) > 0 && <Notice tone="info">{usd(overview.finances!.waiting)} is waiting for confirmation.</Notice>}
      </div>

      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <span className="text-sm font-medium">Balance history</span>
          {history.data && <span className="text-xs text-content-subtle">{history.data.total} movements</span>}
        </div>
        {history.isLoading && <p className="p-4 text-sm text-content-subtle">Loading…</p>}
        {history.isError && <div className="p-4"><Notice tone="danger">{(history.error as Error).message}</Notice></div>}
        <table className="w-full text-sm">
          <tbody className="divide-y divide-border">
            {history.data?.items.map((t) => (
              <tr key={t.id} className="hover:bg-surface-muted/50">
                <td className="px-4 py-2 text-content-subtle whitespace-nowrap">{fmtDate(t.created_at)}</td>
                <td className="px-2 py-2">{KIND[t.kind] ?? t.kind}</td>
                <td className="px-2 py-2 text-content-muted truncate max-w-[260px]">
                  {t.campaign_id ? (
                    <button type="button" className="hover:text-primary" onClick={() => onOpenCampaign(t.campaign_id!)}>{t.app_name} · #{t.campaign_remote_id}</button>
                  ) : t.campaign_remote_id ? `#${t.campaign_remote_id}` : ''}
                </td>
                <td className={`px-4 py-2 text-right tabular-nums font-medium ${t.amount >= 0 ? 'text-success' : 'text-content'}`}>
                  {t.amount >= 0 ? '+' : '−'}{usd(Math.abs(t.amount))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {history.data && history.data.total > 50 && (
          <div className="px-4 py-3 border-t border-border flex justify-end items-center gap-2 text-sm">
            <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>← Newer</Button>
            <span className="text-content-muted">{page + 1} / {Math.ceil(history.data.total / 50)}</span>
            <Button size="sm" variant="secondary" disabled={(page + 1) * 50 >= history.data.total} onClick={() => setPage(page + 1)}>Older →</Button>
          </div>
        )}
      </Card>
    </div>
  );
}

function InvoiceCard({ inv, onCopy }: {
  inv: { amount: number; wallet: string | null; url: string | null; expires: string | null; network: string | null };
  onCopy: (t: string) => void;
}) {
  return (
    <div className="p-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/20 space-y-2 text-sm">
      <div className="flex items-baseline justify-between">
        <span className="font-semibold tabular-nums">{usd(inv.amount)}</span>
        {inv.expires && <span className="text-xs text-content-subtle">until {fmtDate(inv.expires)}</span>}
      </div>
      {inv.network && <div className="text-xs text-content-muted">{inv.network}</div>}
      {inv.wallet && (
        <button type="button" onClick={() => onCopy(inv.wallet!)} title="Copy" className="w-full text-left font-mono text-xs break-all p-2 rounded bg-surface border border-border hover:border-border-strong">
          {inv.wallet} <span className="text-content-subtle">⧉</span>
        </button>
      )}
      {inv.url && <a href={inv.url} target="_blank" rel="noreferrer" className="inline-block text-primary text-sm hover:underline">Open payment page ↗</a>}
    </div>
  );
}
